import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, PenLine } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ARTIKEL } from "@/data/artikel";

function ArticleBody({ article, onBack }) {
    return (
        <div className="pt-24 lg:pt-32" data-testid="artikel-baca">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <button
                    onClick={onBack}
                    data-testid="artikel-kembali"
                    className="btn-ghost -ml-1 inline-flex"
                >
                    <ArrowLeft className="h-4 w-4" /> Semua artikel
                </button>

                <Reveal>
                    <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-accent">
                        {article.tag}
                    </span>
                    <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl">
                        {article.judul}
                    </h1>
                    <p className="mt-4 flex items-center gap-2 text-sm text-ink-muted">
                        <CalendarDays className="h-4 w-4" /> {article.tanggal}
                        <span className="text-ink/30">•</span>
                        Yuhuku Kopi
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-8 flex flex-col gap-5 text-[1.02rem] leading-relaxed text-ink/80">
                        {article.isi.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-ink/5 bg-white px-6 py-5 shadow-soft sm:px-8">
                        <p className="font-semibold text-ink">
                            Masih haus? Intip menu andalan kami.
                        </p>
                        <Link to="/menu" data-testid="artikel-cta-menu" className="btn-primary">
                            Lihat Menu <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </div>
    );
}

export default function Artikel() {
    const [openId, setOpenId] = useState(null);
    const article = ARTIKEL.find((a) => a.id === openId);

    if (article) return <ArticleBody article={article} onBack={() => setOpenId(null)} />;

    const [terbaru, ...lainnya] = ARTIKEL;

    return (
        <div className="pt-24 lg:pt-32" data-testid="artikel-page">
            <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <span className="eyebrow">Dari cafe</span>
                </Reveal>
                <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
                    <Reveal delay={0.08}>
                        <h1 className="font-display text-5xl font-extrabold tracking-tighter text-ink sm:text-6xl lg:text-7xl">
                            Artikel<span className="text-accent">.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="max-w-md pb-2 text-sm leading-relaxed text-ink-muted">
                            Cerita, tips, dan obrolan ringan seputar kopi —
                            ditulis pelan-pelan dari cafe, diperbarui tiap
                            minggu.
                        </p>
                    </Reveal>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
                <Reveal>
                    <button
                        onClick={() => setOpenId(terbaru.id)}
                        data-testid="artikel-card-terbaru"
                        className="group block w-full overflow-hidden rounded-[2rem] border border-ink/5 bg-white text-left shadow-soft transition-shadow duration-300 hover:shadow-lift"
                    >
                        <div className="grid md:grid-cols-5">
                            <div className="flex flex-col justify-between gap-8 p-8 md:col-span-3 sm:p-10">
                                <div>
                                    <span className="rounded-full bg-accent-soft px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-accent">
                                        Terbaru · {terbaru.tag}
                                    </span>
                                    <h2 className="mt-5 font-display text-2xl font-extrabold leading-snug tracking-tight text-ink sm:text-4xl">
                                        {terbaru.judul}
                                    </h2>
                                    <p className="mt-4 leading-relaxed text-ink-muted">
                                        {terbaru.ringkasan}
                                    </p>
                                </div>
                                <span className="inline-flex items-center gap-2 text-sm font-bold text-accent transition-all group-hover:gap-3.5">
                                    Baca artikel <ArrowRight className="h-4 w-4" />
                                </span>
                            </div>
                            <div className="hidden overflow-hidden md:col-span-2 md:block">
                                <img
                                    src="/photos/matcha.jpg"
                                    alt={terbaru.judul}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                        </div>
                    </button>
                </Reveal>

                {lainnya.length > 0 && (
                    <div className="mt-8 grid gap-6 sm:grid-cols-2">
                        {lainnya.map((a, i) => (
                            <Reveal key={a.id} delay={i * 0.08}>
                                <button
                                    onClick={() => setOpenId(a.id)}
                                    data-testid={`artikel-card-${a.id}`}
                                    className="card-clean block w-full p-7 text-left"
                                >
                                    <span className="rounded-full border border-ink/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-muted">
                                        {a.tag}
                                    </span>
                                    <h3 className="mt-4 font-display text-xl font-extrabold leading-snug tracking-tight text-ink">
                                        {a.judul}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-ink-muted">
                                        {a.ringkasan}
                                    </p>
                                    <p className="mt-5 flex items-center gap-2 text-xs text-ink-muted">
                                        <CalendarDays className="h-3.5 w-3.5" /> {a.tanggal}
                                    </p>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                )}

                <Reveal delay={0.1}>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-dashed border-ink/15 px-6 py-5 sm:px-8">
                        <p className="flex items-center gap-2.5 font-semibold text-ink">
                            <PenLine className="h-4 w-4 text-accent" />
                            Artikel baru tiap minggu — tulisan dari belakang
                            counter.
                        </p>
                        <Link to="/kontak" data-testid="artikel-cta-kontak" className="btn-ghost">
                            Kirim ide artikel <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </div>
    );
}